import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const PRESETS = [
  {
    key: "body",
    labelKey: "clamp_preset_body",
    minSize: 16,
    maxSize: 18,
    minVw: 320,
    maxVw: 1280,
  },
  {
    key: "h1",
    labelKey: "clamp_preset_h1",
    minSize: 28,
    maxSize: 56,
    minVw: 320,
    maxVw: 1280,
  },
  {
    key: "h2",
    labelKey: "clamp_preset_h2",
    minSize: 22,
    maxSize: 40,
    minVw: 320,
    maxVw: 1280,
  },
  {
    key: "lead",
    labelKey: "clamp_preset_lead",
    minSize: 18,
    maxSize: 24,
    minVw: 320,
    maxVw: 1280,
  },
  {
    key: "spacing",
    labelKey: "clamp_preset_spacing",
    minSize: 16,
    maxSize: 48,
    minVw: 320,
    maxVw: 1280,
  },
];

const ROOT_PX = 16;

const formatNumber = (n, digits = 4) => {
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  const fixed = n.toFixed(digits);
  return fixed.replace(/\.?0+$/, "");
};

const parsePositive = (raw, fallback) => {
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const parseNonNegative = (raw, fallback) => {
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const CssClampGenerator = () => {
  const { t } = useTranslation();
  const [minSize, setMinSize] = useState("16");
  const [maxSize, setMaxSize] = useState("24");
  const [minVw, setMinVw] = useState("320");
  const [maxVw, setMaxVw] = useState("1280");
  const [unit, setUnit] = useState("rem"); // 'rem' or 'px'
  const [previewVw, setPreviewVw] = useState(800);

  const minSizePx = parseNonNegative(minSize, 0);
  const maxSizePx = parseNonNegative(maxSize, 0);
  const minVwPx = parsePositive(minVw, 320);
  const maxVwPx = parsePositive(maxVw, 1280);

  const validationError = useMemo(() => {
    if (!Number.isFinite(parseFloat(minSize)) || parseFloat(minSize) < 0) {
      return t("clamp_error_min_size");
    }
    if (!Number.isFinite(parseFloat(maxSize)) || parseFloat(maxSize) < 0) {
      return t("clamp_error_max_size");
    }
    if (minSizePx > maxSizePx) {
      return t("clamp_error_min_gt_max");
    }
    if (!Number.isFinite(parseFloat(minVw)) || parseFloat(minVw) <= 0) {
      return t("clamp_error_min_vw");
    }
    if (!Number.isFinite(parseFloat(maxVw)) || parseFloat(maxVw) <= 0) {
      return t("clamp_error_max_vw");
    }
    if (minVwPx >= maxVwPx) {
      return t("clamp_error_min_vw_gt_max");
    }
    return null;
  }, [minSize, maxSize, minVw, maxVw, minSizePx, maxSizePx, minVwPx, maxVwPx, t]);

  const computation = useMemo(() => {
    if (validationError) return null;

    // Linear interpolation between (minVw, minSize) and (maxVw, maxSize), in px.
    const slopePxPerPx = (maxSizePx - minSizePx) / (maxVwPx - minVwPx);
    const interceptPx = minSizePx - slopePxPerPx * minVwPx;
    const slopeVw = slopePxPerPx * 100; // px per 1% of viewport == vw

    const toUnit = (px) =>
      unit === "rem" ? px / ROOT_PX : px;
    const unitSuffix = unit;

    const minOut = `${formatNumber(toUnit(minSizePx))}${unitSuffix}`;
    const maxOut = `${formatNumber(toUnit(maxSizePx))}${unitSuffix}`;
    const interceptOut = `${formatNumber(toUnit(interceptPx))}${unitSuffix}`;
    const slopeOut = `${formatNumber(slopeVw)}vw`;

    const preferred = interceptPx === 0
      ? slopeOut
      : interceptPx < 0
        ? `${slopeOut} - ${formatNumber(Math.abs(toUnit(interceptPx)))}${unitSuffix}`
        : `${interceptOut} + ${slopeOut}`;

    return {
      slopePxPerPx,
      interceptPx,
      slopeVw,
      minOut,
      maxOut,
      preferred,
      css: `clamp(${minOut}, ${preferred}, ${maxOut})`,
    };
  }, [validationError, minSizePx, maxSizePx, minVwPx, maxVwPx, unit]);

  const previewPx = useMemo(() => {
    if (!computation) return 0;
    const raw = computation.slopePxPerPx * previewVw + computation.interceptPx;
    return Math.min(Math.max(raw, minSizePx), maxSizePx);
  }, [computation, previewVw, minSizePx, maxSizePx]);

  const applyPreset = (preset) => {
    setMinSize(String(preset.minSize));
    setMaxSize(String(preset.maxSize));
    setMinVw(String(preset.minVw));
    setMaxVw(String(preset.maxVw));
  };

  const reset = () => {
    setMinSize("16");
    setMaxSize("24");
    setMinVw("320");
    setMaxVw("1280");
    setUnit("rem");
    setPreviewVw(800);
  };

  const copy = async () => {
    if (!computation) {
      toast.error(t("clamp_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(computation.css);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const copyAsDeclaration = async () => {
    if (!computation) {
      toast.error(t("clamp_nothing_to_copy"));
      return;
    }
    const text = `font-size: ${computation.css};`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("clamp_title")}
            </h1>
            <p className="text-gray-600">{t("clamp_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("clamp_presets_label")}
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <StandardButton
                    key={p.key}
                    onClick={() => applyPreset(p)}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t(p.labelKey)}
                  </StandardButton>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="clamp-min-size">
                  {t("clamp_min_size_label")}
                </label>
                <input
                  id="clamp-min-size"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={minSize}
                  onChange={(e) => setMinSize(e.target.value)}
                  aria-label={t("clamp_min_size_label")}
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <span className="text-xs text-gray-500">{t("clamp_size_help")}</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="clamp-max-size">
                  {t("clamp_max_size_label")}
                </label>
                <input
                  id="clamp-max-size"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={maxSize}
                  onChange={(e) => setMaxSize(e.target.value)}
                  aria-label={t("clamp_max_size_label")}
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <span className="text-xs text-gray-500">{t("clamp_size_help")}</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="clamp-min-vw">
                  {t("clamp_min_vw_label")}
                </label>
                <input
                  id="clamp-min-vw"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step="any"
                  value={minVw}
                  onChange={(e) => setMinVw(e.target.value)}
                  aria-label={t("clamp_min_vw_label")}
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <span className="text-xs text-gray-500">{t("clamp_vw_help")}</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="clamp-max-vw">
                  {t("clamp_max_vw_label")}
                </label>
                <input
                  id="clamp-max-vw"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step="any"
                  value={maxVw}
                  onChange={(e) => setMaxVw(e.target.value)}
                  aria-label={t("clamp_max_vw_label")}
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <span className="text-xs text-gray-500">{t("clamp_vw_help")}</span>
              </div>

              <div className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("clamp_output_unit_label")}
                </span>
                <div className="flex gap-2">
                  <StandardButton
                    onClick={() => setUnit("rem")}
                    type={unit === "rem" ? "primary" : "secondary"}
                    variant={unit === "rem" ? undefined : "outlined"}
                    size="small"
                  >
                    rem
                  </StandardButton>
                  <StandardButton
                    onClick={() => setUnit("px")}
                    type={unit === "px" ? "primary" : "secondary"}
                    variant={unit === "px" ? undefined : "outlined"}
                    size="small"
                  >
                    px
                  </StandardButton>
                </div>
                <span className="text-xs text-gray-500">
                  {t("clamp_unit_help", { root: ROOT_PX })}
                </span>
              </div>
            </div>

            {validationError && (
              <div
                role="alert"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2"
              >
                {validationError}
              </div>
            )}

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("clamp_css_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={reset}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("clamp_reset")}
                  </StandardButton>
                  <StandardButton
                    onClick={copyAsDeclaration}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!computation}
                  >
                    {t("clamp_copy_declaration")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!computation}
                  >
                    {t("clamp_copy_value")}
                  </StandardButton>
                </div>
              </div>
              <pre className="w-full min-h-[3rem] p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-all text-gray-800">
                {computation ? computation.css : (
                  <em className="text-gray-400 not-italic">
                    {t("clamp_output_empty")}
                  </em>
                )}
              </pre>
            </div>

            {computation && (
              <div className="flex flex-col gap-2 bg-white p-4 rounded-md border border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label
                    htmlFor="clamp-preview-vw"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("clamp_preview_label")}
                  </label>
                  <span className="text-xs font-mono text-gray-500">
                    {t("clamp_preview_at", {
                      vw: previewVw,
                      size: formatNumber(previewPx, 2),
                    })}
                  </span>
                </div>
                <input
                  id="clamp-preview-vw"
                  type="range"
                  min={Math.max(120, Math.round(minVwPx / 2))}
                  max={Math.round(maxVwPx * 1.5)}
                  step={10}
                  value={previewVw}
                  onChange={(e) => setPreviewVw(parseInt(e.target.value, 10))}
                  aria-label={t("clamp_preview_label")}
                  className="w-full"
                />
                <div
                  className="text-indigo-700 break-words"
                  style={{ fontSize: `${previewPx}px`, lineHeight: 1.2 }}
                >
                  {t("clamp_preview_sample")}
                </div>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("clamp_note_how")}</p>
              <p>{t("clamp_note_unit")}</p>
              <p>{t("clamp_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CssClampGenerator;
